import { Users, Pencil } from 'lucide-react';
import type { Team } from '../types';
import { formatCurrency } from '../utils/helpers';

interface TeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  teamName: string;
  setTeamName: (value: string) => void;
  ownerName: string;
  setOwnerName: (value: string) => void;
  ownerEmail: string;
  setOwnerEmail: (value: string) => void;
  budget: number;
  setBudget: (value: number) => void;
  mode?: 'create' | 'edit';
  editingTeam?: Team | null;
  saving?: boolean;
}

const TeamModal = ({
  isOpen,
  onClose,
  onSubmit,
  teamName,
  setTeamName,
  ownerName,
  setOwnerName,
  ownerEmail,
  setOwnerEmail,
  budget,
  setBudget,
  mode = 'create',
  editingTeam,
  saving = false
}: TeamModalProps) => {
  if (!isOpen) return null;

  const isEdit = mode === 'edit';
  const title = isEdit ? 'Edit Team' : 'Create New Team';
  const Icon = isEdit ? Pencil : Users;
  const buttonText = isEdit ? 'Save Changes' : 'Create Team';
  const buttonColor = isEdit ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-700';

  // For edit mode, show budget difference
  const budgetDiff = editingTeam ? budget - editingTeam.totalBudget : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white p-6 rounded-xl w-full max-w-sm shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-lg ${isEdit ? 'bg-indigo-100' : 'bg-green-100'}`}>
            <Icon className={`w-5 h-5 ${isEdit ? 'text-indigo-600' : 'text-green-600'}`} />
          </div>
          <h3 className="font-bold text-lg text-slate-800">{title}</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Team Name</label>
            <input
              className="w-full border border-slate-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter team name"
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Owner Name</label>
            <input
              className="w-full border border-slate-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter owner name"
              value={ownerName}
              onChange={e => setOwnerName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Owner Email (used as login)
            </label>
            <input
              className="w-full border border-slate-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="owner@example.com"
              type="email"
              value={ownerEmail}
              onChange={e => setOwnerEmail(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">
              Team username and password will use this email.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Total Budget (pts)
              {isEdit && budgetDiff !== 0 && (
                <span className={`ml-2 text-xs ${budgetDiff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ({budgetDiff > 0 ? '+' : '-'}{formatCurrency(Math.abs(budgetDiff))} pts change)
                </span>
              )}
            </label>
            <input
              className="w-full border border-slate-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              type="number"
              placeholder="Enter budget"
              value={budget}
              onChange={e => setBudget(Number(e.target.value))}
              min={0}
            />
            {isEdit && editingTeam && (
              <p className="text-xs text-slate-500 mt-1">
                Current balance: {formatCurrency(editingTeam.currentBalance)} pts → New balance: {formatCurrency(editingTeam.currentBalance + budgetDiff)} pts
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={saving || !teamName.trim() || !ownerName.trim() || !ownerEmail.trim()}
            className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${buttonColor}`}
          >
            {saving ? 'Saving...' : buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Export with both names for backward compatibility
export default TeamModal;
export { TeamModal as NewTeamModal };
